import React, { useState, useEffect } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import AddCircleOutlineTwoToneIcon from '@mui/icons-material/AddCircleOutlineTwoTone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupIcon from '@mui/icons-material/Group';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StarsIcon from '@mui/icons-material/Stars';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LayersIcon from '@mui/icons-material/Layers'; // For Master menu icon and Bundle Management
import DiscountIcon from '@mui/icons-material/Discount'; // For Coupons menu icon
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'; // For Bulk Order Management icon
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn'; // NEW: For Return Process Management icon
import Whatshot from '@mui/icons-material/Whatshot'; // For Flash Sale icon
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/VOTOlogo.png';

interface SlideMenuProps {
  mobileOpen: boolean;
  onToggle: () => void;
}

const SlideMenu: React.FC<SlideMenuProps> = ({ mobileOpen, onToggle }) => {
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showAdminUsersMenu, setShowAdminUsersMenu] = useState(false);
  const [showMasterMenu, setShowMasterMenu] = useState(false);
  const [showCouponsMenu, setShowCouponsMenu] = useState(false); // New state for Coupons menu
  const [adminPermissions, setAdminPermissions] = useState<any>({});
  const location = useLocation();
  const navigate = useNavigate();

  // Get user role - check both admin_type and user_role
  const userRole = (
    localStorage.getItem('admin_type') ||
    localStorage.getItem('user_role') ||
    'operation'
  ).toLowerCase().trim();

  const isSuperAdmin = userRole.includes('super');
  const isAdmin = userRole === 'admin';
  const isOperation = userRole === 'operation';
  const isVendor = userRole === 'vendor';

  console.log('👤 Current user role:', userRole);
  console.log('🏆 Is Super Admin:', isSuperAdmin);
  console.log('🏪 Is Operation:', isOperation);
  console.log('🏪 Is Vendor:', isVendor);
  console.log('👨‍💼 Is Admin:', isAdmin);

  // Auto-redirect users to appropriate pages
  useEffect(() => {
    // REDIRECTION LOGIC:
    // 1. Operation users should ALWAYS be redirected to Product Management
    // They should NEVER see Dashboard or any other page
    if (isOperation) {
      console.log('🔄 Checking redirect for Operation user...');
      console.log('📍 Current path:', location.pathname);

      // List of allowed paths for operation users
      const allowedPaths = ['/productManagement', '/adminOrderManagement', '/bulkOrderManagement'];

      if (!allowedPaths.includes(location.pathname)) {
        console.log('🔄 Redirecting Operation user to Product Management');
        navigate('/productManagement', { replace: true });
      }
    }

    // 2. Vendor users should ALWAYS be redirected to Product Management
    if (isVendor && location.pathname !== '/productManagement' && location.pathname !== '/bundleManagment' && location.pathname !== '/bulkOrderManagement' && location.pathname !== '/returnprocessManagement') {
      console.log('🔄 Redirecting Vendor user to Product Management');
      navigate('/productManagement', { replace: true });
    }

    // 3. Super Admin and Admin can access Dashboard and other pages normally
    // No redirection needed for them
  }, [isOperation, isVendor, location.pathname, navigate]);

  useEffect(() => {
    console.log('👤 Current user role:', userRole);
    console.log('🏆 Is Super Admin:', isSuperAdmin);
    console.log('🏪 Is Vendor:', isVendor);

    // SUPER ADMIN FIX: If user is super admin, bypass all permission checks
    if (isSuperAdmin) {
      console.log('✅ SUPER ADMIN DETECTED - Granting full access to all menus');
      // Set empty permissions - we'll handle super admin in hasAccess function
      setAdminPermissions({});
      return;
    }

    // Operation users: Only Product Management, Order Management, and Bulk Order Management
    if (isOperation) {
      console.log('✅ Operation detected - granting Product Management, Order Management & Bulk Order Management access only');
      setAdminPermissions({});
      return;
    }

    // Vendors have access to Product Management, Bundle Management, Bulk Order Management, and Return Process Management
    if (isVendor) {
      console.log('✅ Vendor detected - granting Product Management, Bundle Management, Bulk Order Management & Return Process Management access');
      setAdminPermissions({});
      return;
    }

    // Check for custom role mappings in localStorage (for admin users)
    const roleMappingsStr = localStorage.getItem('role_mappings');
    if (roleMappingsStr) {
      try {
        const roleMappings = JSON.parse(roleMappingsStr);
        const currentMapping = roleMappings.find(
          (m: any) => m.admin_type?.toLowerCase().trim() === userRole
        );

        if (currentMapping) {
          console.log('✅ Found custom role mapping:', currentMapping);
          const rights = (currentMapping.rights || '')
            .split(',')
            .map((r: string) => r.trim().toLowerCase());

          const modules = (currentMapping.module_or_screen || '')
            .split(',')
            .map((m: string) => m.trim().toLowerCase());

          const customPermissions: any = {};

          modules.forEach((moduleId: string) => {
            if (moduleId === 'all') {
              // Grant all permissions
              getAllMenuItems().forEach((item) => {
                customPermissions[item.id] = {
                  hasAccess: true,
                  canWrite: rights.some((r) =>
                    ['add', 'edit', 'delete', 'write'].includes(r)
                  ),
                };
              });
            } else {
              customPermissions[moduleId] = {
                hasAccess: true,
                canWrite: rights.some((r) =>
                  ['add', 'edit', 'delete', 'write'].includes(r)
                ),
              };
            }
          });

          setAdminPermissions(customPermissions);
          console.log('📋 Custom permissions loaded:', customPermissions);
          return;
        }
      } catch (error) {
        console.error('❌ Error parsing role mappings:', error);
      }
    }

    // Use default permissions if no custom mapping found
    console.log('📋 Using default role permissions');
    setAdminPermissions({});
  }, [userRole, isSuperAdmin, isVendor, isOperation]);

  // Get all menu items including master items
  const getAllMenuItems = () => [
    {
      id: 'dashboard',
      name: 'Dashboard',
      path: '/adminDashboard',
      icon: <DashboardCustomizeOutlinedIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'user_list',
      name: 'User List',
      path: '/Userlist',
      icon: <GroupIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'product_management',
      name: 'Manage product list',
      path: '/productManagement',
      icon: <InventoryIcon />,
      isMaster: true,
      isCoupon: false,
    },
    {
      id: 'bundle_management', // NEW: Bundle Management
      name: 'Bundle Management',
      path: '/bundleManagment',
      icon: <LayersIcon />,
      isMaster: true,
      isCoupon: false,
    },
    {
      id: 'category_management',
      name: 'Category Management',
      path: '/categoryManagement',
      icon: <CategoryOutlinedIcon />,
      isMaster: true,
      isCoupon: false,
    },
    {
      id: 'unit_management',
      name: 'Unit Management',
      path: '/unitManagement',
      icon: <AddCircleOutlineTwoToneIcon />,
      isMaster: true, // MOVED TO MASTER
      isCoupon: false,
    },
    // Order Management moved to regular menu (NOT master)
    {
      id: 'order_management',
      name: 'Order Management',
      path: '/adminOrderManagement',
      icon: <GavelIcon />,
      isMaster: false,
      isCoupon: false,
    },
    // NEW: Bulk Order Management
    {
      id: 'bulk_order_management',
      name: 'Bulk Order Request',
      path: '/bulkOrderManagement',
      icon: <ShoppingCartIcon />,
      isMaster: false,
      isCoupon: false,
    },
    // NEW: Return Process Management
    {
      id: 'return_process_management',
      name: 'Return Process Management',
      path: '/returnprocessManagement',
      icon: <AssignmentReturnIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'warranty_management',
      name: 'Warranty Management',
      path: '/WarrantyManagement',
      icon: <VerifiedUserIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'subcategory_management',
      name: 'Sub Category Management',
      path: '/subcategorymanagement',
      icon: <CategoryOutlinedIcon />,
      isMaster: true,
      isCoupon: false,
    },
    {
      id: 'delivery_agent_management',
      name: 'Delivery Agent Management',
      path: '/DeliveryAgentManagement',
      icon: <AirportShuttleIcon />,
      isMaster: true, // MOVED TO MASTER
      isCoupon: false,
    },
    {
      id: 'warehouse_management',
      name: 'Ware house Management',
      path: '/WarehouseManagement',
      icon: <WarehouseIcon />,
      isMaster: true,
      isCoupon: false,
    },
    {
      id: 'banner_management',
      name: 'Banner Management',
      path: '/banner',
      icon: <ViewCarouselOutlinedIcon />,
      isMaster: true, // MOVED TO MASTER
      isCoupon: false,
    },
    {
      id: 'subbanner_management',
      name: 'Sub Banner Management',
      path: '/subbanner',
      icon: <ViewCarouselOutlinedIcon />,
      isMaster: false,
      isCoupon: false,
      hidden: true, // HIDDEN AS REQUESTED
    },
    {
      id: 'review',
      name: 'Review',
      path: '/review',
      icon: <StarsIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'guest_order_list',
      name: 'Guest User Details',
      path: '/guestOrderlist',
      icon: <AccountCircleIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'guest_user_order',
      name: 'Guest Users Order List',
      path: '/guestUserOrder',
      icon: <PeopleAltIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'set_credit',
      name: 'Set Credit for user',
      path: '/SetCredit',
      icon: <CreditScoreOutlinedIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'pickupPending',
      name: 'Set to Agent Pick UP',
      path: '/pickupPending',
      icon: <AutoFixHighIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'product_warehouse_management',
      name: 'Product Warehouse Management',
      path: '/ProductWarehouseManagement',
      icon: <StorefrontIcon />,
      isMaster: true,
      isCoupon: false,
    },
    {
      id: 'deal_of_the_day',
      name: 'Deal of the day',
      path: '/dealoftheday',
      icon: <AutoAwesomeIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'coupon_management',
      name: 'Coupon Management',
      path: '/couponManagement',
      icon: <DiscountIcon />,
      isMaster: false,
      isCoupon: true, // MOVED TO COUPONS MENU
    },
    {
      id: 'admin_coupon_users',
      name: 'Coupons Users List', // RENAMED AS REQUESTED
      path: '/AdminCouponUsers',
      icon: <PeopleAltIcon />,
      isMaster: false,
      isCoupon: true, // MOVED TO COUPONS MENU
    },
    {
      id: 'reward_management',
      name: 'Reward Management',
      path: '/rewardManagement',
      icon: <MilitaryTechIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'reward_settings_management',
      name: 'Reward Settings Management',
      path: '/RewardSettingsManagement',
      icon: <SettingsIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'admin_rewards',
      name: 'User Reward Management',
      path: '/AdminRewards',
      icon: <ManageAccountsIcon />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'flash_sale',  // FLASH SALE - Regular menu (not master, not coupon)
      name: 'Flash Sale',
      path: '/flashSale',
      icon: <Whatshot />,
      isMaster: false,
      isCoupon: false,
    },
    {
      id: 'delivery_management',
      name: 'Delivery Management',
      path: '/deliveryManagement',
      icon: <AirportShuttleIcon />,
      isMaster: true, // MOVED TO MASTER
      isCoupon: false,
    },
    {
      id: 'shipping_management',
      name: 'Shipping Management',
      path: '/shippingManagement',
      icon: <PublicOutlinedIcon />,
      isMaster: true, // MOVED TO MASTER
      isCoupon: false,
    },
    {
      id: 'delivery_settings',
      name: 'Delivery Settings',
      path: '/deliverySettings',
      icon: <SettingsIcon />,
      isMaster: false,
      isCoupon: false,
    },
  ];

  // Get master menu items only
  const getMasterMenuItems = () => {
    return getAllMenuItems().filter(item => item.isMaster && !item.hidden);
  };

  // Get coupon menu items only
  const getCouponMenuItems = () => {
    return getAllMenuItems().filter(item => item.isCoupon && !item.hidden);
  };

  // Get non-master, non-coupon menu items (regular menu items) - EXCLUDE DASHBOARD HERE
  const getRegularMenuItems = () => {
    return getAllMenuItems().filter(item => 
      !item.isMaster && 
      !item.isCoupon && 
      !item.hidden &&
      item.id !== 'dashboard' // EXCLUDE DASHBOARD FROM REGULAR ITEMS
    );
  };

  // Check if user has access to a menu
  const hasAccess = (menuId: string) => {
    console.log(`🔍 Checking access for ${menuId} for user role: ${userRole}`);
    
    // BULK ORDER MANAGEMENT: Allow access to ALL user types (no restrictions)
    if (menuId === 'bulk_order_management') {
      console.log('✅ Bulk Order Management - Access granted to all users');
      return true;
    }

    // FLASH SALE: Super Admin and Admin ONLY
    if (menuId === 'flash_sale') {
      const hasAccess = isSuperAdmin || isAdmin;
      console.log(`🔥 Flash Sale access check: ${hasAccess} (Super Admin: ${isSuperAdmin}, Admin: ${isAdmin})`);
      return hasAccess;
    }

    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) {
      console.log('✅ Super Admin - Full access granted');
      return true;
    }

    // Vendors: Product Management, Bundle Management, Bulk Order Management, and Return Process Management
    if (isVendor) {
      const allowed = menuId === 'product_management' || menuId === 'bundle_management' || menuId === 'bulk_order_management' || menuId === 'return_process_management';
      console.log(`🛒 Vendor access check for ${menuId}: ${allowed}`);
      return allowed;
    }

    // OPERATION USERS: ONLY Product Management, Order Management, and Bulk Order Management
    // NO access to Bundle Management, Return Process Management, and Flash Sale
    if (isOperation) {
      const allowedMenuIds = ['product_management', 'order_management', 'bulk_order_management'];
      const allowed = allowedMenuIds.includes(menuId);
      console.log(`👷 Operation access check for ${menuId}: ${allowed}`);
      return allowed;
    }

    // If custom permissions exist, use them
    if (Object.keys(adminPermissions).length > 0) {
      const hasCustomAccess = adminPermissions[menuId]?.hasAccess || false;
      console.log(`📋 Custom permissions check for ${menuId}: ${hasCustomAccess}`);
      return hasCustomAccess;
    }

    // Admin users have access to all menus by default including Flash Sale
    if (isAdmin) {
      console.log(`✅ Admin - Access granted to ${menuId}`);
      return true;
    }

    console.log(`❌ No access for ${menuId}`);
    return false;
  };

  // Check if user can write to a menu
  const hasWrite = (menuId: string) => {
    // BULK ORDER MANAGEMENT: Allow write access to ALL user types (no restrictions)
    if (menuId === 'bulk_order_management') {
      return true;
    }

    // FLASH SALE: Super Admin and Admin have write access
    if (menuId === 'flash_sale') {
      return isSuperAdmin || isAdmin;
    }

    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) {
      return true;
    }

    // Vendors: Full write access to allowed menus
    if (isVendor) {
      return menuId === 'product_management' || menuId === 'bundle_management' || menuId === 'bulk_order_management' || menuId === 'return_process_management';
    }

    // Operation users: Full write access to allowed menus
    if (isOperation) {
      const allowedMenuIds = ['product_management', 'order_management', 'bulk_order_management'];
      return allowedMenuIds.includes(menuId);
    }

    // If custom permissions exist, use them
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.canWrite || false;
    }

    // Admin users have write access to all menus by default
    if (isAdmin) return true;

    return false;
  };

  // Check Master menu access
  const hasMasterMenuAccess = () => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO access to Master menu
    if (isOperation) return false;

    // Vendors have access to Product Management and Bundle Management in Master menu
    if (isVendor) return true;

    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      // Check if user has access to any master item
      return getMasterMenuItems().some(item => adminPermissions[item.id]?.hasAccess);
    }

    // Default: Admin has access to all master items
    if (isAdmin) return true;

    return false;
  };

  // Check Coupons menu access
  const hasCouponsMenuAccess = () => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO access to Coupons menu
    if (isOperation) return false;

    // Vendors have no access to Coupons
    if (isVendor) return false;

    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      // Check if user has access to any coupon item
      return getCouponMenuItems().some(item => adminPermissions[item.id]?.hasAccess);
    }

    // Default: Admin has access to all coupon items
    if (isAdmin) return true;

    return false;
  };

  // Check Admin Users section access
  const hasAdminUsersAccess = () => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO access to Admin Users
    if (isOperation) return false;

    // Vendors have no access to Admin Users
    if (isVendor) return false;

    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return (
        adminPermissions['role_master']?.hasAccess ||
        adminPermissions['role_mapping']?.hasAccess ||
        adminPermissions['register_admin']?.hasAccess ||
        adminPermissions['admin_user_list']?.hasAccess
      );
    }

    // Default: Only super admin has access to Admin Users
    return false; // Only super admin (already handled above)
  };

  const hasAdminMenuAccess = (menuId: string) => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO access to Admin menus
    if (isOperation) return false;

    // Vendors have no access to Admin menus
    if (isVendor) return false;

    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.hasAccess || false;
    }

    // Default: Only super admin
    return false;
  };

  const hasAdminMenuWrite = (menuId: string) => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO write access to Admin menus
    if (isOperation) return false;

    // Vendors have no write access to Admin menus
    if (isVendor) return false;

    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.canWrite || false;
    }

    // Default: Only super admin
    return false;
  };

  // Check Settings menu access
  const hasSettingsAccess = () => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO access to Settings
    if (isOperation) return false;

    // Vendors have no access to Settings
    if (isVendor) return false;

    // Check for custom permissions
    if (Object.keys(adminPermissions).length > 0) {
      return (
        adminPermissions['smtp_settings']?.hasAccess ||
        adminPermissions['global_settings']?.hasAccess ||
        adminPermissions['settings']?.hasAccess
      );
    }

    // Default: Super admin and admin have access
    return isAdmin;
  };

  const hasSettingsMenuAccess = (menuId: string) => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO access to Settings
    if (isOperation) return false;

    // Vendors have no access to Settings
    if (isVendor) return false;

    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.hasAccess || false;
    }

    // Default: Super admin and admin
    return isAdmin;
  };

  const hasSettingsMenuWrite = (menuId: string) => {
    // SUPER ADMIN FIX: Always return true for super admin
    if (isSuperAdmin) return true;

    // Operation users: NO write access to Settings
    if (isOperation) return false;

    // Vendors have no write access to Settings
    if (isVendor) return false;

    // Check for custom permissions first
    if (Object.keys(adminPermissions).length > 0) {
      return adminPermissions[menuId]?.canWrite || false;
    }

    // Default: Super admin and admin
    return isAdmin;
  };

  const menuStyle = {
    py: 1.25,
    px: 2,
    borderRadius: '8px',
    mx: 1,
    mb: 0.5,
    color: '#303030',
    '&:hover': { backgroundColor: '#f1f1f1' },
    '&.Mui-selected': {
      backgroundColor: '#dfe3e8',
      color: '#008060',
      fontWeight: 600,
      '& .MuiListItemIcon-root': { color: '#008060' },
      '& .MuiListItemText-primary': { fontWeight: 600 },
    },
  };

  const lockedStyle = {
    ...menuStyle,
    opacity: 0.4,
    cursor: 'not-allowed',
    '&:hover': { backgroundColor: 'transparent' },
  };

  const MenuItem = ({ item }: { item: any }) => {
    const isActive = location.pathname === item.path;
    const access = hasAccess(item.id);
    const write = hasWrite(item.id);

    console.log(`📊 MenuItem ${item.name} (${item.id}): access=${access}, write=${write}, userRole=${userRole}, isAdmin=${isAdmin}`);

    const handleClick = (e: React.MouseEvent) => {
      if (!access) {
        e.preventDefault();
        // Show alert or redirect
        alert('You do not have permission to access this page.');
        // For operation users, redirect to Product Management
        if (isOperation) {
          navigate('/productManagement');
        }
        // For Admin users, they should have access, so don't redirect
      }
    };

    if (!access) {
      return null; // Don't render the menu item at all if no access
    }

    return (
      <ListItemButton
        component={Link}
        to={item.path}
        selected={isActive}
        sx={menuStyle}
        onClick={handleClick}
      >
        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.name}
          primaryTypographyProps={{ fontSize: '0.9rem' }}
        />
        {!write && !isSuperAdmin && !isOperation && !isVendor && (
          <Chip
            label="View"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              backgroundColor: '#ffa726',
              color: '#fff',
            }}
          />
        )}
      </ListItemButton>
    );
  };

  // Master Menu Item component
  const MasterMenuItem = ({ item }: { item: any }) => {
    const isActive = location.pathname === item.path;
    const access = hasAccess(item.id);
    const write = hasWrite(item.id);

    const handleClick = (e: React.MouseEvent) => {
      if (!access) {
        e.preventDefault();
        alert('You do not have permission to access this page.');
        if (isOperation) {
          navigate('/productManagement');
        }
      }
    };

    if (!access) {
      return null; // Don't render the menu item at all if no access
    }

    return (
      <ListItemButton
        component={Link}
        to={item.path}
        selected={isActive}
        sx={{ ...menuStyle, pl: 4 }}
        onClick={handleClick}
      >
        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.name}
          primaryTypographyProps={{ fontSize: '0.9rem' }}
        />
        {!write && !isSuperAdmin && !isOperation && !isVendor && (
          <Chip
            label="View"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              backgroundColor: '#ffa726',
              color: '#fff',
            }}
          />
        )}
      </ListItemButton>
    );
  };

  // Coupons Menu Item component
  const CouponMenuItem = ({ item }: { item: any }) => {
    const isActive = location.pathname === item.path;
    const access = hasAccess(item.id);
    const write = hasWrite(item.id);

    const handleClick = (e: React.MouseEvent) => {
      if (!access) {
        e.preventDefault();
        alert('You do not have permission to access this page.');
        if (isOperation) {
          navigate('/productManagement');
        }
      }
    };

    if (!access) {
      return null; // Don't render the menu item at all if no access
    }

    return (
      <ListItemButton
        component={Link}
        to={item.path}
        selected={isActive}
        sx={{ ...menuStyle, pl: 4 }}
        onClick={handleClick}
      >
        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.name}
          primaryTypographyProps={{ fontSize: '0.9rem' }}
        />
        {!write && !isSuperAdmin && !isOperation && !isVendor && (
          <Chip
            label="View"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              backgroundColor: '#ffa726',
              color: '#fff',
            }}
          />
        )}
      </ListItemButton>
    );
  };

  // Filter menu items based on access
  const getAccessibleRegularMenuItems = () => {
    const regularItems = getRegularMenuItems();
    
    console.log('📋 All regular items:', regularItems.map(item => item.id));
    
    // For super admin, show all items
    if (isSuperAdmin) {
      console.log('👑 Super Admin - showing all regular items');
      return regularItems;
    }
    
    // For operation users: only show allowed items
    if (isOperation) {
      const allowedIds = ['order_management', 'bulk_order_management'];
      const filtered = regularItems.filter(item => allowedIds.includes(item.id));
      console.log('👷 Operation - filtered regular items:', filtered.map(item => item.id));
      return filtered;
    }
    
    // For vendor users: only show allowed items
    if (isVendor) {
      const allowedIds = ['bulk_order_management', 'return_process_management'];
      const filtered = regularItems.filter(item => allowedIds.includes(item.id));
      console.log('🛒 Vendor - filtered regular items:', filtered.map(item => item.id));
      return filtered;
    }
    
    // For admin with custom permissions
    if (Object.keys(adminPermissions).length > 0) {
      const filtered = regularItems.filter(item => adminPermissions[item.id]?.hasAccess);
      console.log('📋 Admin with custom permissions - filtered regular items:', filtered.map(item => item.id));
      return filtered;
    }
    
    // For regular admin, show all (including flash_sale)
    if (isAdmin) {
      console.log('👨‍💼 Admin - showing all regular items');
      return regularItems;
    }
    
    console.log('❌ No regular items for this user');
    return [];
  };

  const getAccessibleMasterMenuItems = () => {
    const masterItems = getMasterMenuItems();
    
    // For super admin, show all items
    if (isSuperAdmin) {
      return masterItems;
    }
    
    // For operation users: only show Product Management
    if (isOperation) {
      const allowedIds = ['product_management'];
      return masterItems.filter(item => allowedIds.includes(item.id));
    }
    
    // For vendor users: only show Product Management and Bundle Management
    if (isVendor) {
      const allowedIds = ['product_management', 'bundle_management'];
      return masterItems.filter(item => allowedIds.includes(item.id));
    }
    
    // For admin with custom permissions
    if (Object.keys(adminPermissions).length > 0) {
      return masterItems.filter(item => adminPermissions[item.id]?.hasAccess);
    }
    
    // For regular admin, show all
    if (isAdmin) {
      return masterItems;
    }
    
    return [];
  };

  const getAccessibleCouponMenuItems = () => {
    const couponItems = getCouponMenuItems();
    
    // For super admin, show all items
    if (isSuperAdmin) {
      return couponItems;
    }
    
    // Operation and vendor users don't see coupon menu at all
    if (isOperation || isVendor) {
      return [];
    }
    
    // For admin with custom permissions
    if (Object.keys(adminPermissions).length > 0) {
      return couponItems.filter(item => adminPermissions[item.id]?.hasAccess);
    }
    
    // For regular admin, show all
    if (isAdmin) {
      return couponItems;
    }
    
    return [];
  };

  // Check if we should show master menu section
  const shouldShowMasterMenu = () => {
    const accessibleMasterItems = getAccessibleMasterMenuItems();
    return accessibleMasterItems.length > 0;
  };

  // Check if we should show coupons menu section
  const shouldShowCouponsMenu = () => {
    const accessibleCouponItems = getAccessibleCouponMenuItems();
    return accessibleCouponItems.length > 0;
  };

  // Special handling for Operation users
  const getOperationUserMenuItems = () => {
    if (!isOperation) return [];
    
    return [
      {
        id: 'product_management',
        name: 'Manage product list',
        path: '/productManagement',
        icon: <InventoryIcon />,
      },
      {
        id: 'order_management',
        name: 'Order Management',
        path: '/adminOrderManagement',
        icon: <GavelIcon />,
      },
      {
        id: 'bulk_order_management',
        name: 'Bulk Order Request',
        path: '/bulkOrderManagement',
        icon: <ShoppingCartIcon />,
      }
    ];
  };

  // Special handling for Vendor users
  const getVendorUserMenuItems = () => {
    if (!isVendor) return [];
    
    return [
      {
        id: 'product_management',
        name: 'Manage product list',
        path: '/productManagement',
        icon: <InventoryIcon />,
      },
      {
        id: 'bundle_management',
        name: 'Bundle Management',
        path: '/bundleManagment',
        icon: <LayersIcon />,
      },
      {
        id: 'bulk_order_management',
        name: 'Bulk Order Request',
        path: '/bulkOrderManagement',
        icon: <ShoppingCartIcon />,
      },
      {
        id: 'return_process_management',
        name: 'Return Process Management',
        path: '/returnprocessManagement',
        icon: <AssignmentReturnIcon />,
      }
    ];
  };

  return (
    <Box
      sx={{
        width: 260,
        height: '100vh',
        backgroundColor: '#fff',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={logo} alt="VOTO Logo" style={{ height: 40 }} />
        </Box>
        {mobileOpen && (
          <IconButton onClick={onToggle} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Role Badge */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Chip
          icon={
            isVendor ? (
              <StorefrontIcon sx={{ fontSize: 18, color: '#fff !important' }} />
            ) : isSuperAdmin ? (
              <AdminPanelSettingsIcon sx={{ fontSize: 18, color: '#fff !important' }} />
            ) : isAdmin ? (
              <SecurityIcon sx={{ fontSize: 18, color: '#fff !important' }} />
            ) : (
              <AssignmentIndIcon sx={{ fontSize: 18, color: '#fff !important' }} />
            )
          }
          label={
            isVendor
              ? 'Vendor'
              : isSuperAdmin
              ? 'Super Admin'
              : isAdmin
              ? 'Admin'
              : 'Operation'
          }
          sx={{
            width: '100%',
            backgroundColor: isVendor
              ? '#4caf50'
              : isSuperAdmin
              ? '#f44336'
              : isAdmin
              ? '#2196f3'
              : '#ff9800',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.875rem',
          }}
        />
      </Box>

      {/* Menu Items */}
      <List
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 1,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#bbb',
            borderRadius: '3px',
          },
        }}
      >
        {/* Dashboard - Only ONE Dashboard menu for Super Admin and Admin users */}
        {!isOperation && !isVendor && (
          <MenuItem item={{
            id: 'dashboard',
            name: 'Dashboard',
            path: '/adminDashboard',
            icon: <DashboardCustomizeOutlinedIcon />,
            isMaster: false,
            isCoupon: false,
          }} />
        )}

        {/* Special handling for Operation users */}
        {isOperation && getOperationUserMenuItems().map((item) => (
          <MenuItem 
            key={item.id} 
            item={{
              ...item,
              isMaster: false,
              isCoupon: false
            }} 
          />
        ))}

        {/* Special handling for Vendor users */}
        {isVendor && getVendorUserMenuItems().map((item) => (
          <MenuItem 
            key={item.id} 
            item={{
              ...item,
              isMaster: false,
              isCoupon: false
            }} 
          />
        ))}

        {/* Master Menu Dropdown - Only show if user has access to any master items and NOT operation/vendor */}
        {!isOperation && !isVendor && shouldShowMasterMenu() && (
          <>
            <ListItemButton
              onClick={() => setShowMasterMenu(!showMasterMenu)}
              sx={menuStyle}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <LayersIcon />
              </ListItemIcon>
              <ListItemText
                primary="Master"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
              />
              {showMasterMenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={showMasterMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {getAccessibleMasterMenuItems().map((item) => (
                  <MasterMenuItem key={item.id} item={item} />
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Regular menu items (non-master, non-coupon items) - Filtered based on access (for Admin and Super Admin only) */}
        {!isOperation && !isVendor && getAccessibleRegularMenuItems().map((item) => (
          <MenuItem key={item.id} item={item} />
        ))}

        {/* Coupons Menu Dropdown - Only show if user has access to any coupon items (for Admin and Super Admin only) */}
        {!isOperation && !isVendor && shouldShowCouponsMenu() && (
          <>
            <ListItemButton
              onClick={() => setShowCouponsMenu(!showCouponsMenu)}
              sx={menuStyle}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <DiscountIcon />
              </ListItemIcon>
              <ListItemText
                primary="Coupons"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
              />
              {showCouponsMenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={showCouponsMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {getAccessibleCouponMenuItems().map((item) => (
                  <CouponMenuItem key={item.id} item={item} />
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Admin Users Dropdown - Hidden for Operation and Vendor users */}
        {!isOperation && !isVendor && hasAdminUsersAccess() && (
          <>
            <ListItemButton
              onClick={() => setShowAdminUsersMenu(!showAdminUsersMenu)}
              sx={menuStyle}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <ManageAccountsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Admin Users"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
              />
              {showAdminUsersMenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={showAdminUsersMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* Admin User List - NEW */}
                {hasAdminMenuAccess('admin_user_list') && (
                  <ListItemButton
                    component={Link}
                    to="/adminUserList"
                    selected={location.pathname === '/adminUserList'}
                    sx={{ ...menuStyle, pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      <PeopleAltIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Admin User List"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                    {!hasAdminMenuWrite('admin_user_list') && !isSuperAdmin && (
                      <Chip
                        label="View"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: '#ffa726',
                          color: '#fff',
                        }}
                      />
                    )}
                  </ListItemButton>
                )}

                {/* Role Master */}
                {hasAdminMenuAccess('role_master') && (
                  <ListItemButton
                    component={Link}
                    to="/roleMaster"
                    selected={location.pathname === '/roleMaster'}
                    sx={{ ...menuStyle, pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Role Master"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                    {!hasAdminMenuWrite('role_master') && !isSuperAdmin && (
                      <Chip
                        label="View"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: '#ffa726',
                          color: '#fff',
                        }}
                      />
                    )}
                  </ListItemButton>
                )}

                {/* Role Mapping */}
                {hasAdminMenuAccess('role_mapping') && (
                  <ListItemButton
                    component={Link}
                    to="/roleMapping"
                    selected={location.pathname === '/roleMapping'}
                    sx={{ ...menuStyle, pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      <AssignmentIndIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Role Mapping"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                    {!hasAdminMenuWrite('role_mapping') && !isSuperAdmin && (
                      <Chip
                        label="View"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: '#ffa726',
                          color: '#fff',
                        }}
                      />
                    )}
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}

        {/* Settings Dropdown - Hidden for Operation and Vendor users */}
        {!isOperation && !isVendor && hasSettingsAccess() && (
          <>
            <ListItemButton
              onClick={() => setShowSettingsMenu(!showSettingsMenu)}
              sx={menuStyle}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
              />
              {showSettingsMenu ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={showSettingsMenu} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* SMTP Settings */}
                {hasSettingsMenuAccess('smtp_settings') && (
                  <ListItemButton
                    component={Link}
                    to="/smtpsettings"
                    selected={location.pathname === '/smtpsettings'}
                    sx={{ ...menuStyle, pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="SMTP Settings"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                    {!hasSettingsMenuWrite('smtp_settings') && !isSuperAdmin && (
                      <Chip
                        label="View"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: '#ffa726',
                          color: '#fff',
                        }}
                      />
                    )}
                  </ListItemButton>
                )}

                {/* Global Settings */}
                {hasSettingsMenuAccess('global_settings') && (
                  <ListItemButton
                    component={Link}
                    to="/globalSettings"
                    selected={location.pathname === '/globalSettings'}
                    sx={{ ...menuStyle, pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      <LanguageIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Global Settings"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                    {!hasSettingsMenuWrite('global_settings') && !isSuperAdmin && (
                      <Chip
                        label="View"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: '#ffa726',
                          color: '#fff',
                        }}
                      />
                    )}
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  );
};

export default SlideMenu;